/* eslint-disable complexity */
import { FIELD_TYPES } from './field_types';
import { VALIDATION_RULES } from './validation_rules';
import { CreateFormFieldObject, CreateFormObject, FormObject } from '../../params/redux/form';
import { FieldType, FieldTypesObject } from './form_builder_iterfaces';
import { FONT_FAMILIES, getDefaultPlaceholder } from './font_families';

interface FieldConfig {
    id: string;
    type: string;
    label: string;
    placeholder?: string;
    fontFamily?: string;
    labelColor?: string;
    styling?: {
        labelStyle?: {
            fontSize?: string;
        };
        inputStyle?: {
            padding?: string;
            borderRadius?: string;
            borderColor?: string;
        };
    };
    acceptedTypes?: string;
    heading_size?: string;
    btnAlign?: string;
    btnSize?: string;
    bgColor?: string;
    options?: string[];
    optionCount?: number;
}

const searchBarQuery = (query: string, setFilteredFieldTypes: (filtered: FieldTypesObject) => void) => {
    const lowercaseQuery = query.trim().toLowerCase();
    if (!lowercaseQuery) {
        setFilteredFieldTypes(FIELD_TYPES);
        return;
    }

    const filtered: FieldTypesObject = Object.entries(FIELD_TYPES).reduce((acc, [category, fields]) => {
        const matchingFields = fields.filter(({ label, type }) => label.toLowerCase().includes(lowercaseQuery) ||
            type.toLowerCase().includes(lowercaseQuery) ||
            category.toLowerCase().replace(/_/gu, ' ')
                .includes(lowercaseQuery));

        if (matchingFields.length > 0) {
            acc[category] = matchingFields;
        }
        return acc;
    }, {} as FieldTypesObject);

    setFilteredFieldTypes(filtered);
};

const createFormConfigData = (formFields: CreateFormObject) => {
    const data = {
        version: '2.0',
        metadata: {
            fieldCount: formFields.fields.length,
            requiredFieldCount: formFields.fields.filter((f) => f.required).length,
            title: formFields.formTitle
        },
        validation: {
            global: {
                // Global form-level validations
                maxSubmissionsPerUser: null,
                submissionTimeframe: null,
                ipRestriction: null
            },
            fields: formFields.fields.map((field) => ({
                id: field.id,
                type: field.type,
                required: field.required,
                validationRules: {
                    // Field-specific validation configurations
                    minLength: field.minLength,
                    maxLength: field.maxLength,
                    min: field.min,
                    max: field.max,
                    precision: field.precision,
                    pattern: field.pattern,
                    patternMessage: field.patternMessage,
                    allowedDomains: field.allowedDomains,
                    countryCode: field.countryCode,
                    allowedFileTypes: field.acceptedTypes,
                    heading_size: field.heading_size,
                    btnAlign: field.btnAlign || 'left',
                    btnSize: field.btnSize || 'small',
                    bgColor: field.bgColor || '#000'
                },
                errorMessages: {
                    required: `${field.label} is required`,
                    invalid: `Invalid ${field.label}`
                }
            }))
        },
        fields: formFields.fields.map((field) => ({
            ...field,
            styling: {
                labelStyle: {
                    fontFamily: field.fontFamily,
                    color: field.labelColor,
                    fontSize: field.fontSize || '16px'
                },
                inputStyle: {
                    padding: field.padding || '8px',
                    borderRadius: field.borderRadius || '4px',
                    borderColor: field.borderColor || '#cccccc'
                }
            }
        }))
    };
    return data;
};

const makeDataForSelection = (field: FieldType) => {
    return {
        id: `field-${field.type}-${Date.now()}`,
        type: field.type,
        label: `${field.type.charAt(0).toUpperCase() + field.type.slice(1)} Label`,
        placeholder: '',
        required: false,
        fontFamily: 'inherit',
        labelColor: '#000000',
        heading_size: 'h1',
        sub_heading_text: 'Sub Text',
        sub_heading_color: '#000000',
        sub_heading_font: 'inherit',
        options: field.type === 'checkbox' || field.type === 'radio' ? ['Option 1', 'Option 2'] : [],
        optionCount: field.type === 'checkbox' || field.type === 'radio' ? 2 : 0,
        min: field.type === 'slider' ? 0 : null,
        max: field.type === 'slider' ? 100 : null,
        step: field.type === 'slider' ? 1 : null,
        maxStars: field.type === 'rating' ? 5 : null,
        currency: field.type === 'currency' ? 'USD' : null,
        acceptedTypes: field.type === 'file'
            ? '.pdf,.doc,.docx'
            : field.type === 'image' ? 'image/*' : null
    };
};

const parseFormConfigData = (form: FormObject): CreateFormObject => {
    try {
        const config = JSON.parse(form.form_json);

        // Extract form title and description from metadata
        const formTitle = form.title || '';
        const formDescription = config.metadata?.description || '';
        // Map fields from JSON to CreateFormFieldObject

        const formFields: CreateFormFieldObject[] = config.fields.map((field: FieldConfig) => {
            // Find validation rules correctly using `config.validation.fields`
            const validation = config.validation?.fields.find((rule: { id: string }) => rule.id === field.id) || {};

            return {
                id: field.id,
                type: field.type,
                label: field.label,
                placeholder: field.placeholder,
                required: validation.required || false,
                fontFamily: field.fontFamily,
                labelColor: field.labelColor || '#fff',
                fontSize: field.styling?.labelStyle?.fontSize || '16px',
                padding: field.styling?.inputStyle?.padding || '8px',
                borderRadius: field.styling?.inputStyle?.borderRadius || '4px',
                borderColor: field.styling?.inputStyle?.borderColor || '#cccccc',
                min: validation.validationRules?.min ?? null,
                max: validation.validationRules?.max ?? null,
                step: validation.validationRules?.step ?? null,
                maxStars: validation.validationRules?.maxStars ?? null,
                pattern: validation.validationRules?.pattern || '',
                patternMessage: validation.validationRules?.patternMessage || '',
                acceptedTypes: validation.validationRules?.allowedFileTypes || field.acceptedTypes,
                heading_size: field.heading_size || 'h1',
                btnAlign: field.btnAlign || 'left',
                btnSize: field.btnSize || 'small',
                bgColor: field.bgColor || '#000',
                options: field.options || [],
                optionCount: field.optionCount || 0
            };
        });
        const hasSubmit = formFields.some((field) => field.type === 'submit');
        const tempFormFields = [...formFields];

        if (!hasSubmit) {
            tempFormFields.push({
                id: `field-submit-${Date.now()}`,
                type: 'submit',
                label: 'Submit',
                btnAlign: 'left',
                fontFamily: 'inherit',
                labelColor: '#fff',
                bgColor: '#000000',
                btnSize: 'small'
            });
        }

        return {
            formTitle,
            formDescription,
            isEditing: true,
            assignees: form.assignees,
            formTemplateId: form.template_id,
            pipelineId: form.pipeline_id || 0,
            stageId: form.stage_id || 0,
            redirectMessage: form.redirect_msg,
            redirectUrl: form.redirect_url,
            fields: tempFormFields,
            showTitle: 0
        };
    } catch (error) {
        return {
            formTitle: '',
            formDescription: '',
            isEditing: false,
            formTemplateId: 2,
            pipelineId: 0,
            stageId: 0,
            redirectMessage: '',
            redirectUrl: '',
            assignees: [],
            showTitle: 0,
            fields: []
        };
    }
};

export {
    FIELD_TYPES,
    VALIDATION_RULES,
    FONT_FAMILIES,
    searchBarQuery,
    createFormConfigData,
    getDefaultPlaceholder,
    makeDataForSelection,
    parseFormConfigData
};